import type { ReactNode } from "react";
import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  Link,
  Outlet,
} from "@tanstack/react-router";
import { AppProviders, AppShell } from "../components/AppShell";
import type { RouterContext } from "../router";
import appCss from "../styles/globals.css?url";
import iconSidebarCss from "../styles/icon-sidebar.css?url";

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      {
        title: "FlowState Automate",
      },
      {
        name: "description",
        content:
          "AI-powered business automation — CRM, tasks, accounting, calendar, and more",
      },
      {
        name: "theme-color",
        content: "#3b82f6",
      },
      {
        name: "apple-mobile-web-app-capable",
        content: "yes",
      },
      {
        name: "apple-mobile-web-app-status-bar-style",
        content: "black-translucent",
      },
      {
        name: "mobile-web-app-capable",
        content: "yes",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico" },
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: iconSidebarCss },
      { rel: "manifest", href: "/manifest.json" },
      {
        rel: "apple-touch-icon",
        href: "/flowstate-logo.png",
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function NotFoundComponent() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-muted-foreground">Page not found</p>
      <Link to="/" className="text-primary underline underline-offset-4 hover:text-primary/80">
        Go home
      </Link>
    </div>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Pre-paint theme bootstrap: applies persisted theme classes/vars before
            first paint so dark-mode users don't get a light flash (FOUC).
            Mirrors @flowstate/ui ThemeProvider + ExtendedThemeProvider writes. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function () {
                try {
                  var d = document.documentElement;
                  var theme = localStorage.getItem('flowstate-ui-theme');
                  var resolved = theme === 'dark' || theme === 'light'
                    ? theme
                    : (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
                  d.classList.add(resolved);

                  var color = localStorage.getItem('flowstate-ui-color-theme');
                  var validColors = ['flowstate-blue', 'purple-edge', 'emerald', 'amber', 'rose'];
                  d.classList.add('theme-' + (validColors.indexOf(color) !== -1 ? color : 'flowstate-blue'));

                  var glowMult = { subtle: 0.5, normal: 1, intense: 1.5, max: 2 }[localStorage.getItem('flowstate-ui-glow-intensity')];
                  if (glowMult) d.style.setProperty('--glow-multiplier', String(glowMult));

                  var perf = JSON.parse(localStorage.getItem('flowstate-ui-performance-mode') || 'false') === true;
                  if (perf) d.classList.add('performance-mode');

                  var glass = JSON.parse(localStorage.getItem('flowstate-ui-glass-opacity') || '0.65');
                  if (typeof glass === 'number' && isFinite(glass)) {
                    var clamped = Math.max(0.2, Math.min(0.95, glass));
                    d.style.setProperty('--holo-card-alpha', String(clamped));
                    d.style.setProperty('--card-opacity', String(clamped));
                  }

                  var holo = JSON.parse(localStorage.getItem('flowstate-ui-holographic-mode') || 'true') === true;
                  var holoIntensity = JSON.parse(localStorage.getItem('flowstate-ui-holographic-intensity') || '"medium"');
                  if (holo && !perf) {
                    d.classList.add('holographic-mode');
                    if (['subtle', 'medium', 'intense'].indexOf(holoIntensity) !== -1) {
                      d.classList.add('holographic-' + holoIntensity);
                    }
                  }
                } catch (e) {}
              })();
            `,
          }}
        />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <RootDocument>
      <AppProviders>
        <AppShell>
          <Outlet />
        </AppShell>
      </AppProviders>
    </RootDocument>
  );
}
